import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IClub } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { Helmet } from "react-helmet";
import InfiniteScroll from "react-infinite-scroll-component";

import { helperService } from "src/services/helper.service";
import { localStorageService } from "src/services/local-storage.service";
import { apiService } from "src/services";

import SearchFilter, { ISearchOption, IActiveFilter } from "src/components/SearchFilter/SearchFilter";
import ClubCard from "./components/ClubCard/ClubCard";
import FavoriteClubModal from "src/components/FavoriteClubModal/FavoriteClubModal";
import { States, Provinces } from "src/components/Dropdown/Dropdown";

import styles from "./KennelClubs.module.scss";

const KennelClubs: React.FC = () => {
  const user = helperService.getUser();
  const [searchParams] = useSearchParams();

  const [loaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [favorites, setFavorites] = useState<string[]>(user.favorite_clubs ?? []);
  const [clubs, setClubs] = useState<IClub[]>([]);

  const clubNameParam = searchParams.get("name");
  const clubNameOption: ISearchOption = { label: "Club Name", value: "club_name", helpText: "Name of the Club" };
  const initialFilters: IActiveFilter[] = clubNameParam
      ? [{ filter: clubNameOption, option: clubNameParam }]
      : [];

  const [searchFilters, setSearchFilters] = useState<IActiveFilter[]>(initialFilters);
  
  // Favorite club modal state
  const [showFavoriteModal, setShowFavoriteModal] = useState<boolean>(false);
  const [selectedClub, setSelectedClub] = useState<IClub | null>(null);

  const limit:number = 12;
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [skip, setSkip] = useState<number>(0);

  const [searchReset, setSearchReset] = useState<boolean>(false);

  const searchOptions: ISearchOption[] = [{
      label: "Club Name",
      value: "club_name",
      helpText: "Name of the Club"
  }, {
      label: "City",
      value: "address.city",
      helpText: "City of the Event"
  }, {
      label: "State/Province",
      value: "address.state",
      helpText: "Abbreviation (ON, AL, etc.)",
      options: [...Provinces.map((s: any, index: number) => {
        return s.id;
      }), ...States.map((s: any, index: number) => {
        return s.id;
      })]
  }, {
      label: "Country",
      value: "address.country",
      helpText: "Canada, USA, etc.",
      options: ["Canada", "USA"]
  }];

  const getFavoriteClubQuery = () => {
    let query: any = {
        sort: [
            {
                "attribute_name": "club_name",
                "sort": "asc"
            }
        ],
        $limit: 0,
        $skip: 0,
        query: {
              $and: [],
              $or: []
          }
    };

    for (let i in favorites) {
      query.query.$or.push({
        attribute_name: "_id",
        attribute_value: {
          operator: "$eq",
          value: favorites[i],
        }
      });
    }

    return query;
  }

  const getClubs = () => {
    if (!loading) {
      setLoading(true);

      let query: any = {
          sort: [
              {
                  "attribute_name": "club_name",
                  "sort": "asc"
              }
          ],
          $limit: limit,
          $skip: skip,
          query: {
              $and: [],
              $or: []
          }
      };

      if (skip === 0 && helperService.isLoggedIn() && favorites.length > 0 && searchFilters.length === 0) {
        apiService.getClubs(getFavoriteClubQuery()).then((favoriteClubs) => {
          apiService.getClubs(query).then((response) => {
            setClubs([...favoriteClubs.clubs, ...response.clubs.filter((value: IClub, index: number) => { return !favorites.includes(value._id) })]);
            setHasMore(response.clubs.length === limit);
            setSkip(skip + limit);
          }).finally(() => {
            setLoading(false);
          });
        });
      } else {
        for (const filter of searchFilters) {
            let newQuery: any = {
                attribute_name: filter.filter.value,
                attribute_value: {
                    operator: (filter.filter.value === "start_date" || filter.filter.value === "end_date") ? "$gte" : "$regex",
                    value: filter.option
                }
            };

            if (filter.filter.value && filter.option) {
                query.query.$or!.push(newQuery);
            }
        }

        apiService.getClubs(query).then((response) => {
          setClubs([...clubs, ...response.clubs]);
          setHasMore(response.clubs.length === limit);
          setSkip(skip + limit);
        }).finally(() => {
          setLoading(false);
        });
      }
    }
  }

  const isUserFavoriteClub = (id: string) => {
    return favorites.includes(id);
  }

  const toggleFavorite = (id: string, _favorites: string[]) => {
    let newFavs = JSON.parse(JSON.stringify(_favorites));

    if (newFavs.includes(id) && newFavs.indexOf(id) !== -1) {
      // Removing from favorites - just remove, no modal
      newFavs.splice(newFavs.indexOf(id), 1);
      apiService.updateSelf({ favorite_clubs: newFavs }).then((response) => {
        localStorageService.setItem(localStorageService.USER, JSON.stringify(response));
      });
      setFavorites(newFavs);
    } else {
      // Adding to favorites - add first, then show modal
      newFavs = [...newFavs, id];
      apiService.updateSelf({ favorite_clubs: newFavs }).then((response) => {
        localStorageService.setItem(localStorageService.USER, JSON.stringify(response));
      });
      setFavorites(newFavs);
      
      // Find the club and show the modal
      const club = clubs.find(c => c._id === id);
      if (club) {
        setSelectedClub(club);
        setShowFavoriteModal(true);
      }
    }
  }

  const resetSearch = () => {
    setSkip(0);
    setHasMore(true);
    setClubs([]);
    setSearchReset(true);
  }

  useEffect(() => {
    resetSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters]);

  useEffect(() => {
    if (searchReset) {
      getClubs();
      setSearchReset(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  } , [searchReset]);

  useEffect(() => {
    if (!loaded) {
      setSkip(0);
      setHasMore(true);
      setClubs([]);
      setLoaded(true);
    }
  }, [loaded]);

  return (
      <div className={styles.KennelClubs}>
        <Helmet>
          <title>Clubs</title>
          <meta name="description" content="Browse Kennel Clubs running events and courses on Eagerdog."/>
        </Helmet>
        <div className={styles.inner}>
          <div className={styles.title}>
              <h1>Kennel Clubs</h1>
              <SearchFilter
                  searchOptions={searchOptions}
                  initialFilters={initialFilters}
                  onFilterChange={(filters: IActiveFilter[]) => {
                      setSearchFilters(filters);
                  }}
              />
          </div>
          {loading && <div className={styles.loading} key={0}>
              <div className={styles.list}>
                  <div className={styles.cardSkeleton}></div>
                  <div className={styles.cardSkeleton}></div>
                  <div className={styles.cardSkeleton}></div>
                  <div className={styles.cardSkeleton}></div>
                  <div className={styles.cardSkeleton}></div>
                  <div className={styles.cardSkeleton}></div>
              </div>
          </div>}
          {((!loading && skip === 0) || skip > 0) && (<InfiniteScroll
              className={styles.list}
              dataLength={clubs.length}
              next={getClubs}
              hasMore={hasMore}
              loader={<div className={styles.loading} key={0}></div>}
          >
            {clubs.map((club: IClub, index: number) => {
                    return(
                      <ClubCard
                        key={index}
                        club={club}
                        isFavorite={isUserFavoriteClub(club._id)}
                        onToggleFavorite={(id: string) => { toggleFavorite(id, favorites) }}
                      />
                    );
                })
            }
            {(user?.user_type === Constants.user_type.club_manager ? (clubs.length + 1) : clubs.length) % 3 !== 0 && <div className={styles.cardPlaceholder}></div>}
          </InfiniteScroll>)}
        </div>
        <FavoriteClubModal
          isShown={showFavoriteModal}
          hide={() => { setShowFavoriteModal(false); setSelectedClub(null); }}
          club={selectedClub}
        />
      </div>
    );
};

export default KennelClubs;